import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface FAQ {
  category: string;
  question: string;
  answer: string;
}

interface FAQListProps {
  faqs: FAQ[];
  searchQuery: string;
}

const FAQList = ({ faqs, searchQuery }: FAQListProps) => {
  const { t } = useTranslation();
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  // 高亮搜索关键词
  const highlightText = (text: string, query: string) => {
    if (!query.trim()) return text;
    
    const parts = text.split(new RegExp(`(${query})`, 'gi'));
    return parts.map((part, index) => 
      part.toLowerCase() === query.toLowerCase() 
        ? <mark key={index} className="bg-yellow-200 text-gray-900 px-1 rounded">{part}</mark>
        : part
    );
  };

  return (
    <section className="py-16">
      <div className="max-w-4xl mx-auto px-6">
        {faqs.length === 0 ? (
          <div className="text-center py-20">
            <div className="inline-flex items-center justify-center w-24 h-24 bg-gray-100 rounded-full mb-6">
              <i className="ri-search-line text-5xl text-gray-400"></i>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-3">
              {t('faq:noResults.title')}
            </h3>
            <p className="text-lg text-gray-600">
              {t('faq:noResults.subtitle')}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="bg-white rounded-xl border-2 border-gray-200 overflow-hidden transition-all hover:border-teal-500 hover:shadow-lg"
              >
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full flex items-center justify-between gap-4 p-6 text-left cursor-pointer group"
                >
                  <div className="flex items-start gap-4 flex-1">
                    <div className="flex-shrink-0 w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center group-hover:bg-teal-600 transition-colors">
                      <i className={`ri-question-line text-xl text-teal-600 group-hover:text-white transition-colors`}></i>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 leading-relaxed">
                      {highlightText(faq.question, searchQuery)}
                    </h3>
                  </div>
                  <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center">
                    <i className={`ri-arrow-down-s-line text-2xl text-gray-400 transition-transform ${openIndex === index ? 'rotate-180' : ''}`}></i>
                  </div>
                </button>

                {openIndex === index && (
                  <div className="px-6 pb-6 pl-20">
                    <div className="bg-gray-50 rounded-lg p-6 border-l-4 border-teal-600">
                      <p className="text-base text-gray-700 leading-relaxed whitespace-pre-line">
                        {highlightText(faq.answer, searchQuery)}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* 统计信息 */}
        {faqs.length > 0 && (
          <div className="mt-10 text-center">
            <p className="text-base text-gray-600">
              {t('faq:showingResults', { count: faqs.length })}
            </p>
          </div>
        )}
      </div>
    </section>
  );
};

export default FAQList;